import * as LucideIcons from 'lucide-react';
import { Check, CheckCircle, Download, Edit3, Eye, FileText, Filter, Logs, Plus, RefreshCw, Search, Settings, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NotificationSound } from '../../CommonComponents/CommonNotification';
import { ApiCall, DropDownData } from '../../CommonComponents/CustomHooks';
import { FormButton, InputBox, SelectInputBox } from '../../CommonComponents/InputBox';

const UserGroupAccess = () => {
    const [groupNameValue, setGroupNameValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFolder, setSelectedFolder] = useState('All');
    const [isLoading, setIsLoading] = useState(false);
    const inputrefs = useRef([])

    const [masterCheckboxes, setMasterCheckboxes] = useState({
        view: false,
        add: false,
        edit: false,
        del: false,
        exp: false,
        all: false
    });
    const [formData, setFormData] = useState([]);

    const GetGroupName = DropDownData('GetUserGroup')


    const getMenuUserGroupRight = async (id) => {
        const req = {
            userGroupID: id ? id : ""
        }
        let data = await ApiCall(`/api/User/GetMenuUserGroupRight`, req)
        if (data.status === 'SUCCESS') {
            setFormData(data.getGroupUserRightsMenu)
            setTimeout(() => syncMasterCheckboxes(data.getGroupUserRightsMenu), 0);

        } else {
            setFormData([])
            await NotificationSound(data.status, data.message)
        }
    }

    const permissionColumns = [
        { key: 'view', label: 'View', icon: Eye, color: 'blue', description: 'Can view pages' },
        { key: 'add', label: 'Add', icon: Plus, color: 'green', description: 'Can add new records' },
        { key: 'edit', label: 'Edit', icon: Edit3, color: 'orange', description: 'Can modify records' },
        { key: 'del', label: 'Delete', icon: Trash2, color: 'red', description: 'Can delete records' },
        { key: 'exp', label: 'Export', icon: Download, color: 'purple', description: 'Can export data' },
        { key: 'all', label: 'All', icon: CheckCircle, color: 'gray', description: 'All permissions' }
    ];

    const uniqueFolders = useMemo(() => {
        const folders = [...new Set(formData?.map(item => item.folderName))];
        return ['All', ...folders];
    }, [formData]);

    const ModernCheckbox = ({ checked, onChange, disabled = false, color = 'blue', size = 'md', tooltip = '' }) => {
        const [isHovered, setIsHovered] = useState(false);

        const sizeClasses = {
            sm: 'w-4 h-4',
            md: 'w-5 h-5',
            lg: 'w-6 h-6'
        };

        const colorClasses = {
            blue: checked ? 'bg-blue-600 border-blue-600 shadow-blue-200' : 'bg-white border-gray-300 hover:border-blue-400',
            green: checked ? 'bg-green-600 border-green-600 shadow-green-200' : 'bg-white border-gray-300 hover:border-green-400',
            orange: checked ? 'bg-orange-600 border-orange-600 shadow-orange-200' : 'bg-white border-gray-300 hover:border-orange-400',
            red: checked ? 'bg-red-600 border-red-600 shadow-red-200' : 'bg-white border-gray-300 hover:border-red-400',
            purple: checked ? 'bg-purple-600 border-purple-600 shadow-purple-200' : 'bg-white border-gray-300 hover:border-purple-400',
            gray: checked ? 'bg-gray-600 border-gray-600 shadow-gray-200' : 'bg-white border-gray-300 hover:border-gray-400',
            yellow: checked ? 'bg-yellow-600 border-yellow-600 shadow-yellow-200' : 'bg-white border-gray-300 hover:border-yellow-400'
        };

        return (
            <div className="relative" title={tooltip}>
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onChange(e)}
                    disabled={disabled}
                    className="sr-only"
                />
                <div
                    onClick={!disabled ? () => onChange(!checked) : undefined}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    className={`
            ${sizeClasses[size]} ${colorClasses[color]} rounded-md cursor-pointer transition-all duration-300 transform
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-sm hover:scale-105'}
            ${checked && !disabled ? 'shadow-sm' : ''}
            ${isHovered && !disabled ? 'ring-2 ring-opacity-50' : ''}
            border-2 flex items-center justify-center 
          `}
                >
                    {checked && (
                        <div className="animate-in">
                            {size === 'sm' ? (
                                <Check className="w-2.5 h-2.5 text-white" />
                            ) : (
                                <Check className="w-3 h-3 text-white" />
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const LoadingSpinner = () => (
        <div className="flex items-center justify-center p-8">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading permissions...</span>
        </div>
    );

    const syncMasterCheckboxes = useCallback((data) => {
        const keys = ['view', 'add', 'edit', 'del', 'exp'];
        const updatedMasters = {};
        keys.forEach(key => {
            updatedMasters[key] = data?.every(item => item[key]);
        });
        updatedMasters.all = keys.every(k => updatedMasters[k]);
        setMasterCheckboxes(updatedMasters);
    }, []);

    const handleCheckboxChange = useCallback((pageIndex, key) => {
        setFormData(prevData => {
            const newData = [...prevData];
            const currentItem = { ...newData[pageIndex] };

            if (key === 'all') {
                const newValue = !currentItem[key];
                currentItem.view = newValue;
                currentItem.add = newValue;
                currentItem.edit = newValue;
                currentItem.del = newValue;
                currentItem.exp = newValue;
                currentItem.all = newValue;
            } else {
                currentItem[key] = !currentItem[key];

                if (key !== 'view' && currentItem[key] && !currentItem.view) {
                    currentItem.view = true;
                }

                if (key === 'view' && !currentItem[key]) {
                    currentItem.add = false;
                    currentItem.edit = false;
                    currentItem.del = false;
                    currentItem.exp = false;
                    currentItem.all = false;
                }

                currentItem.all =
                    currentItem.view &&
                    currentItem.add &&
                    currentItem.edit &&
                    currentItem.del &&
                    currentItem.exp;
            }

            newData[pageIndex] = currentItem;
            setTimeout(() => syncMasterCheckboxes(newData), 0);
            return newData;
        });
    }, [syncMasterCheckboxes]);

    const handleMasterCheckbox = useCallback((key) => {
        const newValue = !masterCheckboxes[key];

        setFormData(prevData => {
            let newData;
            if (key === 'all') {
                newData = prevData.map(item => ({
                    ...item,
                    view: newValue,
                    add: newValue,
                    edit: newValue,
                    del: newValue,
                    exp: newValue,
                    all: newValue
                }));
            } else {
                newData = prevData.map(item => {
                    const updatedItem = { ...item, [key]: newValue };
                    if (key !== 'view' && newValue && !updatedItem.view) {
                        updatedItem.view = true;
                    }
                    if (key === 'view' && !newValue) {
                        updatedItem.add = false;
                        updatedItem.edit = false;
                        updatedItem.del = false;
                        updatedItem.exp = false;
                        updatedItem.all = false;
                    }
                    updatedItem.all =
                        updatedItem.view &&
                        updatedItem.add &&
                        updatedItem.edit &&
                        updatedItem.del &&
                        updatedItem.exp;
                    return updatedItem;
                });
            }
            setTimeout(() => syncMasterCheckboxes(newData), 0);
            return newData;
        });
    }, [masterCheckboxes, syncMasterCheckboxes]);

    const handleFolderCheckbox = useCallback((mPageID, checked) => {
        setFormData(prevData =>
            prevData.map(item => {
                if (item.mPageID === mPageID) {
                    return {
                        ...item,
                        view: checked,
                        add: checked,
                        edit: checked,
                        del: checked,
                        exp: checked,
                        all: checked
                    };
                }
                return item;
            })
        );
    }, []);

    const handleSubmit = useCallback(async () => {
        if (!groupNameValue.trim()) {
            NotificationSound('error', 'Please enter a group name', 'UserGroupID', inputrefs);
            return;
        }
        setIsLoading(true);

        try {
            const requestData = {
                userGroupID: groupNameValue ? groupNameValue : "",
                data: formData.map(item => ({
                    pageID: item.pageID,
                    view: item.view,
                    add: item.add,
                    edit: item.edit,
                    del: item.del,
                    exp: item.exp,
                    all: item.all
                }))
            };
            let response = await ApiCall('/api/User/InsertUpdateGroupUserRights', requestData);
            NotificationSound(response?.status, response?.message, response?.focus);
            // NotificationSound(response?.status, `Permissions saved successfully for group: ${groupNameValue}`, "");
            if (response?.status === "SUCCESS") {
                handleReset(false)
            }

        } catch (error) {
            console.error('Error saving permissions:', error);
            NotificationSound('error', 'Failed to save permissions. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [groupNameValue, formData]);

    const handleReset = useCallback((isShowNotification = true) => {
        setGroupNameValue('');
        setSearchTerm('');
        setSelectedFolder('All');
        getMenuUserGroupRight("")
        setMasterCheckboxes({
            view: false,
            add: false,
            edit: false,
            del: false,
            exp: false,
            all: false
        });
        isShowNotification && NotificationSound('info', 'All permissions cleared');
    }, []);

    const stats = useMemo(() => {
        const totalPages = formData.length;
        const pagesWithView = formData.filter(item => item.view).length;
        const pagesWithAdd = formData.filter(item => item.add).length;
        const pagesWithEdit = formData.filter(item => item.edit).length;
        const pagesWithDelete = formData.filter(item => item.del).length;
        const pagesWithExport = formData.filter(item => item.exp).length;
        const pagesWithFullAccess = formData.filter(item => item.all).length;

        const folderStats = [...new Set(formData.map(item => item.folderName))].map(folderName => {
            const folderPages = formData.filter(item => item.folderName === folderName);
            const folderPagesWithAccess = folderPages.filter(item => item.view).length;

            return {
                name: folderName,
                total: folderPages.length,
                withAccess: folderPagesWithAccess,
                percentage: folderPages.length > 0
                    ? Math.round((folderPagesWithAccess / folderPages.length) * 100)
                    : 0
            };
        });
        return {
            totalPages,
            pagesWithView,
            pagesWithAdd,
            pagesWithEdit,
            pagesWithDelete,
            pagesWithExport,
            pagesWithFullAccess,
            folderStats
        };
    }, [formData]);

    const filteredFormData = useMemo(() => {
        let filtered = formData;

        if (selectedFolder !== 'All') {
            filtered = filtered.filter(item => item.folderName === selectedFolder);
        }
        if (searchTerm) {
            filtered = filtered.filter(item =>
                item.page.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.folderName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        return filtered;
    }, [formData, selectedFolder, searchTerm]);

    const renderTableRows = useMemo(() => {
        let currentMPageID = '';
        return filteredFormData.map((item, index) => {
            const originalIndex = formData.findIndex(originalItem => originalItem.pageID === item.pageID);
            const isNewGroup = currentMPageID !== item.mPageID;
            if (isNewGroup) currentMPageID = item.mPageID;
            const IconComponent = LucideIcons[item.icon] || FileText;

            return (
                <tr
                    key={`${item.pageID}-${index}`}
                    className="hover:bg-gray-50 transition-all duration-200 group"
                >
                    {isNewGroup && (
                        <>
                            <td
                                rowSpan={item.rowSpan}
                                className={`px-2 py-2 border-r border-gray-200 bg-gradient-to-r from-${item.color}-100 to-white align-top min-w-48 group-hover:from-${item.color}-100`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded bg-${item.color}-100`}>
                                        <IconComponent className={`w-5 h-5 text-${item.color}-600`} />
                                    </div>
                                    <div>
                                        <div className={`font-semibold text-${item.color}-800`}>
                                            {item.folderName}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            {item.description}
                                        </div>
                                    </div>
                                </div>
                            </td>

                            <td
                                rowSpan={item.rowSpan}
                                className={`px-3 py-4 border-r border-gray-200 bg-gradient-to-r from-${item.color}-100 to-white text-center align-top w-16`}
                            >
                                <ModernCheckbox
                                    checked={item.all}
                                    onChange={(e) => {
                                        handleFolderCheckbox(item.mPageID, e)
                                    }}
                                    color={item.color}
                                    tooltip={`Select all pages in ${item.folderName}`}
                                />
                            </td>
                        </>
                    )}

                    <td className="px-2 py-2 border-r border-gray-200">
                        <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-gray-900">{item.page}</div>
                            {(item.view || item.add || item.edit || item.del || item.exp) && (
                                <div className="flex gap-1">
                                    {item.view && <div className="w-2 h-2 bg-blue-400 rounded-full"></div>}
                                    {item.add && <div className="w-2 h-2 bg-green-400 rounded-full"></div>}
                                    {item.edit && <div className="w-2 h-2 bg-orange-400 rounded-full"></div>}
                                    {item.del && <div className="w-2 h-2 bg-red-400 rounded-full"></div>}
                                    {item.exp && <div className="w-2 h-2 bg-purple-400 rounded-full"></div>}
                                </div>
                            )}
                        </div>
                    </td>

                    {permissionColumns.map(({ key, color, description }) => (
                        <td
                            key={key}
                            className="px-2 py-2 border-r border-gray-200 text-center w-20"
                        >
                            <ModernCheckbox
                                checked={item[key]}
                                onChange={() => handleCheckboxChange(originalIndex, key)}
                                disabled={key !== 'view' && key !== 'all' && !item.view}
                                color={color}
                                tooltip={description}
                            />
                        </td>
                    ))}
                </tr>
            );
        });
    }, [filteredFormData, formData, handleCheckboxChange, handleFolderCheckbox]);

    useEffect(() => {
        //getMenuUserGroupRight()
    }, [])

    useEffect(() => {
        document.title = "GRANDSLAM_QMS : User Group Access";
        inputrefs.current['UserGroupID'].focus()
    }, [])

    return (
        <>
                <div className='bg-white rounded-lg shadow-sm p-6 mb-6'>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Logs  className="w-6 h-6 text-teal-600" />
                            <h2 className="text-xl font-semibold text-gray-800">User Group Access</h2>
                        </div>
                    </div>
                    <div className='card-body pb-1'>
                        <div className='row'>
                            <div className='col-md-12'>
                                <div className="row">
                                    <SelectInputBox
                                        id="UserGroupID"
                                        label='Group Name'
                                    inputrefs={inputrefs}
                                    required={true }
                                        value={groupNameValue || ""}
                                        onChange={(e) => {
                                            setGroupNameValue(e.target.value)
                                            getMenuUserGroupRight(e.target.value);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                inputrefs.current["txtSearchPages"]?.focus();
                                            }
                                        }}
                                    >
                                        <option value="">--Please Select--</option>
                                        {GetGroupName?.data?.dropDownData?.map((item, index) =>
                                            <option key={item.textPart + index} value={item.valuePart}>{item.textPart}</option>
                                        )}
                                    </SelectInputBox>


                                    <InputBox
                                        id="txtSearchPages"
                                        type="text"
                                        label="Search Pages"
                                        placeholder="Search pages..."
                                        value={searchTerm}
                                        inputrefs={inputrefs}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                inputrefs.current["txtFilterFolder"]?.focus();
                                            }
                                        }}
                                        prefixIcon={<Search className="w-3.5 h-3.5 text-gray-400" />}
                                    />

                                    <SelectInputBox
                                        id="txtFilterFolder"
                                        label="Filter by Folder"
                                        inputrefs={inputrefs}
                                        value={selectedFolder || ""}
                                        onChange={(e) => setSelectedFolder(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                inputrefs.current["btnSub"]?.focus();
                                            }
                                        }}
                                        prefixIcon={<Filter className="w-4 h-4 text-gray-400" />}
                                    >
                                        <option value="">--Please Select--</option>
                                        {uniqueFolders.map((folder, index) => (
                                            <option key={folder + index} value={folder}>
                                                {folder}
                                            </option>
                                        ))}
                                    </SelectInputBox>

                                </div>
                            </div>
                        </div>
                        <div className='card-body'>
                            <FormButton
                                id={'btnSub'}
                                inputrefs={inputrefs}
                                handleSubmit={() => handleSubmit()}
                                Export={false}
                                handleClear={() => isLoading ? "" : handleReset()}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6 mt-3">
                        <div className="bg-blue-100 flex items-center justify-between p-3 rounded shadow-sm">
                            <div>
                                <p className="text-sm text-gray-500">Total Pages</p>
                                <p className="text-2xl font-bold text-blue-600">{stats.totalPages}</p>
                            </div>
                            <FileText className="w-8 h-8 text-blue-600" />
                        </div>

                        <div className="bg-blue-100 flex items-center justify-between p-3 rounded shadow-sm">
                            <div>
                                <p className="text-sm text-gray-500">View Access</p>
                                <p className="text-2xl font-bold text-blue-600">{stats.pagesWithView}</p>
                            </div>
                            <Eye className="w-8 h-8 text-blue-600" />
                        </div>

                        <div className="bg-green-100 flex items-center justify-between p-3 rounded shadow-sm">
                            <div>
                                <p className="text-sm text-gray-500">Add Access</p>
                                <p className="text-2xl font-bold text-green-600">{stats.pagesWithAdd}</p>
                            </div>
                            <Plus className="w-8 h-8 text-green-600" />
                        </div>

                        <div className="bg-orange-100 flex items-center justify-between p-3 rounded shadow-sm">
                            <div>
                                <p className="text-sm text-gray-500">Edit Access</p>
                                <p className="text-2xl font-bold text-orange-600">{stats.pagesWithEdit}</p>
                            </div>
                            <Edit3 className="w-8 h-8 text-orange-600" />
                        </div>

                        <div className="bg-red-100 flex items-center justify-between p-3 rounded shadow-sm">
                            <div>
                                <p className="text-sm text-gray-500">Delete Access</p>
                                <p className="text-2xl font-bold text-red-600">{stats.pagesWithDelete}</p>
                            </div>
                            <Trash2 className="w-8 h-8 text-red-600" />
                        </div>

                        <div className="bg-purple-100 flex items-center justify-between p-3 rounded shadow-sm">
                            <div>
                                <p className="text-sm text-gray-500">Full Access</p>
                                <p className="text-2xl font-bold text-purple-600">{stats.pagesWithFullAccess}</p>
                            </div>
                            <CheckCircle className="w-8 h-8 text-purple-600" />
                        </div>
                    </div>
                </div>
                <div className='bg-white rounded-lg shadow-sm p-6 mb-6'>
                    <div className="bg-white rounded shadow-sm overflow-hidden border border-gray-200">
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <Settings className="w-6 h-6" />
                                Permission Matrix
                            </h2>
                            <p className="text-gray-600 mt-1">Configure access permissions for each page and functionality</p>
                        </div>

                        {isLoading ? (
                            <LoadingSpinner />
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-b border-gray-200 ">
                                        <tr>
                                            <th className=" p-2 text-left text-sm font-bold text-gray-700 border-r border-gray-200 min-w-48">
                                                Module
                                            </th>
                                            <th className=" p-2 text-center text-sm font-bold text-gray-700 border-r border-gray-200 w-16">
                                                All
                                            </th>
                                            <th className=" p-2 text-left text-sm font-bold text-gray-700 border-r border-gray-200">
                                                Page Name
                                            </th>

                                            {permissionColumns.map(({ key, label, icon: Icon, color, description }) => (
                                                <th
                                                    key={key}
                                                    className="p-2 text-center text-sm font-bold text-gray-700 border-r border-gray-200 w-20"
                                                    title={description}
                                                >
                                                    <div className="flex flex-col items-center gap-2">
                                                        <div className={`p-2 rounded bg-${color}-100`}>
                                                            <Icon className={`w-4 h-4 text-${color}-600`} />
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-xs">{label}</span>
                                                            <ModernCheckbox
                                                                checked={masterCheckboxes[key]}
                                                                onChange={() => handleMasterCheckbox(key)}
                                                                color={color}
                                                                size="sm"
                                                                tooltip={`Select all ${label.toLowerCase()} permissions`}
                                                            />
                                                        </div>
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y divide-gray-200">
                                        {filteredFormData.length === 0 ? (
                                            <tr>
                                                <td colSpan={permissionColumns.length + 3} className="px-6 py-8 text-center text-gray-500">
                                                    <div className="flex flex-col items-center gap-2">
                                                        <Search className="w-8 h-8 text-gray-400" />
                                                        <p>No pages found matching your search criteria</p>
                                                        <button
                                                            onClick={() => {
                                                                setSearchTerm('');
                                                                setSelectedFolder('All');
                                                            }}
                                                            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                                                        >
                                                            Clear filters
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            renderTableRows
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
        </>
    );
};
export default UserGroupAccess;